/**
 * Short field help — title + body shown on info-icon hover
 * Keys match input/select id attributes
 */

export const FIELD_HELP = {
    dealName: {
        title: 'Deal name',
        body: 'A descriptive label for the opportunity. Example: "Acme — Website Redesign Q3".'
    },
    dealClientId: {
        title: 'Account',
        body: 'The company associated with this deal.'
    },
    dealOwnerUserId: {
        title: 'Sales Rep',
        body: 'The primary sales representative responsible for this opportunity.'
    },
    dealPrimaryContactId: {
        title: 'SPOC',
        body: 'Single point of contact for this deal. Separate from the account SPOC.'
    },
    dealPipeline: {
        title: 'Pipeline',
        body: 'The product or service category. Affects available service lines and reporting.'
    },
    dealServiceLine: {
        title: 'Service line',
        body: 'The specific offering within the selected pipeline. Example: "SEO" under Digital Marketing.'
    },
    dealStage: {
        title: 'Stage',
        body: 'The current step in the sales pipeline.'
    },
    dealForecastCategory: {
        title: 'Forecast category',
        body: 'How finance counts this deal: pipeline, best case, commit, or closed.'
    },
    dealType: {
        title: 'Deal type',
        body: 'Specifies if this is new business, a renewal, or an upsell. Hides or requires fields (e.g. lead source).'
    },
    dealLeadSource: {
        title: 'Lead source',
        body: 'How the opportunity was sourced. Example: referral, inbound, event.'
    },
    dealCampaignSource: {
        title: 'Campaign source',
        body: 'The marketing campaign or channel that generated this opportunity. Example: "LinkedIn Q2 webinar".'
    },
    dealAmount: {
        title: 'Deal amount',
        body: 'Expected value used for pipeline and forecast before contract is final.'
    },
    dealProbability: {
        title: 'Probability %',
        body: 'The estimated likelihood of winning the deal, from 0% to 100%. Adjusting this automatically updates the stage.'
    },
    dealForecastProbability: {
        title: 'Forecast override',
        body: 'Optional % used only for weighted forecast KPIs. Example: stage says 75% but you forecast at 60%.'
    },
    expectedCloseDate: {
        title: 'Expected close Date',
        body: 'The anticipated date when the deal will close. This is separate from project start or billing dates.'
    },
    originalCreatedDate: {
        title: 'Created date',
        body: 'Used for created date filter. Falls back to system created date if not set.'
    },
    dealDate: {
        title: 'Date Created',
        body: 'The date when the deal was created. If not specified, the system creation date is used for filtering.'
    },
    dealNextStep: {
        title: 'Next step',
        body: 'A specific, actionable task to advance the deal. Example: "Send revised SOW by Friday."'
    },
    dealLostReason: {
        title: 'Lost reason',
        body: 'The reason for losing the deal, required when the stage is set to "Closed Lost". Example: "Budget Deferred."'
    },
    dealDescription: {
        title: 'Notes',
        body: 'Internal notes for the deal. Not displayed on the board. Can include meeting summaries, risks, or stakeholder information.'
    },
    dealContractValue: {
        title: 'Contract value',
        body: 'Automatically populated from the Deal Amount. To modify, update the Deal Amount field.'
    },
    dealPaymentTerms: {
        title: 'Payment terms',
        body: 'The payment due date terms for invoices. Required when the deal is "Closed Won". Example: "Net 30".'
    },
    sowSigningDate: {
        title: 'SOW signing date',
        body: 'Auto-filled from Expected Close Date. To change, update the Expected Close Date field above.'
    },
    dealBillingStartDate: {
        title: 'Billing start date',
        body: 'The date when billing for this deal begins. Influences the automated invoice schedule.'
    },
    dealBillingEndDate: {
        title: 'Billing end date',
        body: 'The date when recurring billing ends. Leave blank if no end date is determined.'
    },
    dealPaymentModel: {
        title: 'Payment model',
        body: 'The billing frequency or revenue structure, such as monthly, milestone-based, or ongoing support.'
    },
    dealInvoiceAmount: {
        title: 'Invoice amount',
        body: 'The amount per invoice or per billing period. Example: "$5,000/month".'
    },
    dealCommissionEligible: {
        title: 'Commission eligible',
        body: 'Indicates Whether sales representatives earn commission when invoices are paid on this deal.'
    },
    dealCommissionModel: {
        title: 'Commission model',
        body: 'Defines how commission is calculated, whether as a percentage of revenue or a fixed pool shared among representatives.'
    },
    dealCommissionRate: {
        title: 'Commission rate',
        body: 'The percentage of revenue paid as commission. Example: "8%".'
    },
    dealCommissionFixed: {
        title: 'Fixed commission',
        body: 'The total commission amount in dollars, distributed among representatives based on their percentage allocation.'
    },
    dealEngagementModel: {
        title: 'Engagement Model',
        body: 'Delivery model for the deal: Fixed Price, Time & Material, Retainer, etc. Influences billing cadence and resourcing.'
    },
    dealCommissionBasis: {
        title: 'Commission Basis',
        body: 'What commission is calculated from. Deal Value uses the closed amount; Revenue Schedule prorates across invoices as they are paid.'
    },
    dealPayoutMethod: {
        title: 'Payout Method',
        body: 'When commission accrues. One-Time on close, Per Schedule on each invoice paid, or Custom per your commission policy.'
    },
    dealCurrency: {
        title: 'Currency',
        body: 'Billing currency for the deal. Currently USD only; other currencies are disabled.'
    },
    dealPaymentCount: {
        title: 'Number of Payments',
        body: 'How many invoices will be generated for this payment model.'
    },
    dealPrimaryContactId: {
        title: 'Primary Contact',
        body: 'Main contact for this opportunity. Used as the deal SPOC and for primary outreach.'
    },
    accounts_name: {
        title: 'Account name',
        body: 'The legal or trading name of the company. Example: "Acme Corporation".'
    },
    accounts_ownerUserId: {
        title: 'Account owner',
        body: 'Internal CRM user who owns this account on your team.'
    },
    accounts_relationshipOwnerUserId: {
        title: 'Relationship owner',
        body: 'Internal sales rep who manages the day-to-day relationship. Shown as Sales Rep on the list.'
    },
    accounts_accountType: {
        title: 'Account type',
        body: 'The classification of the account, such as Prospect, Customer, or Partner. Affects required fields.'
    },
    accounts_industry: {
        title: 'Industry',
        body: 'The sector the account belongs to. Example: Software, Healthcare, Retail.'
    },
    accounts_rating: {
        title: 'Rating (heat)',
        body: 'An indicator of engagement level: Hot (high intent), Warm (engaged), or Cold (low priority). Used for sorting on the Kanban board.'
    },
    accounts_phone: {
        title: 'Phone',
        body: 'The primary phone number for the account. Example: "+1 555-0100".'
    },
    accounts_website: {
        title: 'Website',
        body: 'The company\'s main website domain. Example: "acme.com".'
    },
    accounts_leadSource: {
        title: 'Lead source',
        body: 'How the account was originally identified. Example: Referral, Outbound, Event.'
    },
    accounts_estimatedProjectValue: {
        title: 'Estimated project value',
        body: 'An approximate deal size for prospects. Used in commission previews on the account level.'
    },
    accounts_billingCountry: {
        title: 'Billing country',
        body: 'The country for invoicing and tax documentation. Example: "USA".'
    },
    accounts_accountSite: {
        title: 'Account site',
        body: 'Specific site or location label for the account. Example: "Ohio Office" or "HQ".'
    },
    accounts_parentAccountId: {
        title: 'Parent account',
        body: 'Links this account under a parent for hierarchy and roll-up reporting.'
    },
    accounts_accountNumber: {
        title: 'Account number',
        body: 'External identifier from ERP or billing. Example: "ACC-10234".'
    },
    accounts_annualRevenue: {
        title: 'Annual revenue',
        body: 'Company revenue used for segmentation. Example: "$5M".'
    },
    accounts_ownership: {
        title: 'Ownership',
        body: 'Ownership type, e.g. Private, Public, Subsidiary.'
    },
    accounts_employees: {
        title: 'Employees',
        body: 'Headcount for sizing. Example: "50–200".'
    },
    accounts_sicCode: {
        title: 'SIC code',
        body: 'Standard Industrial Classification code.'
    },
    accounts_billingStreet: {
        title: 'Billing street',
        body: 'Street address for invoices.'
    },
    accounts_billingState: {
        title: 'Billing state',
        body: 'State or province for billing address.'
    },
    accounts_billingCity: {
        title: 'Billing city',
        body: 'City for billing address.'
    },
    accounts_billingCode: {
        title: 'Billing postal code',
        body: 'ZIP or postal code for billing address.'
    },
    accounts_technologyStack: {
        title: 'Technology stack',
        body: 'Key platforms used by the account. Example: "React, Shopify".'
    },
    accounts_currentCms: {
        title: 'Current CMS',
        body: 'Current content management system. Example: "WordPress".'
    },
    accounts_currentEcommercePlatform: {
        title: 'E-commerce platform',
        body: 'Current commerce platform. Example: "Magento".'
    },
    accounts_monthlyWebsiteTraffic: {
        title: 'Monthly traffic',
        body: 'Estimated monthly website visits.'
    },
    accounts_lastContactDate: {
        title: 'Last contact date',
        body: 'Date of most recent outreach or touch.'
    },
    accounts_aiReadinessScore: {
        title: 'AI readiness',
        body: 'Score indicating AI adoption potential.'
    },
    accounts_seoAeoGeoOpportunity: {
        title: 'SEO / AEO / GEO',
        body: 'Opportunity notes for search optimization.'
    },
    accounts_notes: {
        title: 'Notes',
        body: 'Internal notes for this account.'
    },
    accounts_status: {
        title: 'Status',
        body: 'Whether the account is active or inactive.'
    },
    accounts_fax: {
        title: 'Fax',
        body: 'Fax number for the account.'
    },
    accounts_email: {
        title: 'Account email',
        body: 'General inbox for the account. Example: "info@company.com".'
    },
    accounts_phoneCountry: {
        title: 'Country code',
        body: 'Phone country dialing prefix.'
    },
    contacts_firstName: {
        title: 'First name',
        body: 'The contact\'s given name. Example: "Jane".'
    },
    contacts_lastName: {
        title: 'Last name',
        body: 'The contact\'s family name. Example: "Smith".'
    },
    contacts_email: {
        title: 'Email',
        body: 'The contact\'s work email address for outreach and activity tracking. Example: "jane@acme.com".'
    },
    contacts_phone: {
        title: 'Phone',
        body: 'The contact\'s direct line or mobile number. Example: "+1 555-0142".'
    },
    contacts_title: {
        title: 'Job title',
        body: 'The contact\'s role within the company. Example: "VP of Marketing".'
    },
    contacts_linkedIn: {
        title: 'LinkedIn',
        body: 'The URL to the contact\'s LinkedIn profile for quick reference.'
    },
    contacts_notes: {
        title: 'Notes',
        body: 'Additional information about the contact, such as preferences or context. Example: "Prefers email; is the decision maker."'
    },
    contacts_accountSelect: {
        title: 'Linked accounts',
        body: 'Accounts this contact is associated with. Used for deal contact selection and SPOC.'
    },
    contacts_phoneCountry: {
        title: 'Country code',
        body: 'Phone prefix for the contact.'
    },
    users_name: {
        title: 'Name',
        body: 'The display name of the CRM user. Example: "Alex Rivera".'
    },
    users_email: {
        title: 'Email',
        body: 'The user\'s email address for login and notifications.'
    },
    users_role: {
        title: 'Role',
        body: 'The user\'s permission level within the CRM, such as Sales, Manager, Admin, or Finance.'
    },
    users_commissionPct: {
        title: 'Default commission %',
        body: 'The default commission percentage pre-filled for new deals owned by this user. Example: "8".'
    }
};
